import React from "react";
import { createContext } from "react";

import Flag, { FlagGroup, AutoDismissFlag, FlagProps } from "@atlaskit/flag";
import Heading from "@atlaskit/heading";
import CrossCircleIcon from "@atlaskit/icon/core/cross-circle";
import StatusSuccessIcon from "@atlaskit/icon/core/success";
import { Text } from "@atlaskit/primitives";

export type CustomFlagProps = FlagProps & {
    iconType: "success" | "error";
    autoDismiss?: boolean;
    styleOverride?: boolean;
};

const CustomFlag = ({
    iconType,
    autoDismiss,
    styleOverride,
    ...props
}: CustomFlagProps) => {
    const icon =
        iconType === "success" ? (
            <StatusSuccessIcon
                label="success"
                color="var(--ds-icon-success)"
                LEGACY_size="large"
            />
        ) : (
            <CrossCircleIcon
                label="error"
                color="var(--ds-icon-danger)"
                LEGACY_size="large"
            />
        );

    // Wrapping text in ADS components resolves WAC and ADS Design System Conflicts for staging and production
    // note while props.actions do not display correctly in localhost, they do in stg and prod
    const styleOverrideProps = styleOverride
        ? {
              title: <Heading size="small">{props.title}</Heading>,
              description: <Text>{props.description}</Text>
          }
        : {};

    if (autoDismiss) {
        return (
            <AutoDismissFlag icon={icon} {...props} {...styleOverrideProps} />
        );
    }
    return <Flag icon={icon} {...props} {...styleOverrideProps} />;
};

interface FlagContextProps {
    showFlag: (config: CustomFlagProps) => void;
}

export const FlagContext = createContext<FlagContextProps>({
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    showFlag: () => {} // for instantiation only
});

export const FlagProvider: React.FC<React.PropsWithChildren> = ({
    children
}) => {
    const [flags, setFlags] = React.useState<Array<CustomFlagProps>>([]);
    const showFlag = (config: CustomFlagProps) => {
        setFlags([config, ...flags]);
    };
    const handleDismiss = () => {
        setFlags(flags.slice(1));
    };

    return (
        <FlagContext.Provider value={{ showFlag }}>
            {children}
            <FlagGroup onDismissed={handleDismiss}>
                {flags.map((props, index) => {
                    return <CustomFlag key={`flag-${index}`} {...props} />;
                })}
            </FlagGroup>
        </FlagContext.Provider>
    );
};

export default {
    Provider: FlagProvider,
    Context: FlagContext
};
